import React from 'react'

import {
  DeliveryIcon,
  LocationIcon,
  RestaurantMenuIcon,
  SplitIcon
} from '@toasttab/buffet-pui-icons'
import parsePhoneNumber, { CountryCode } from 'libphonenumber-js'
import Link from 'src/shared/components/common/link'
import { DeliveryDriver, DiningOptionBehavior } from 'src/apollo/onlineOrdering'
import useTracker from 'src/lib/js/hooks/useTracker'
import { useCustomer } from 'src/public/components/online_ordering/CustomerContextCommon'
import { useCompletedOrder } from 'src/public/components/online_ordering/OrderContext'
import { formatAddress } from 'src/shared/components/common/form_input/LocationInput'
import { useRestaurant } from 'src/shared/components/common/restaurant_context/RestaurantContext'
import { getDisplayBusinessName } from 'src/shared/js/locationUtils'
import { TrackingEvents } from 'src/shared/components/common/tracking/TrackingEvents'
import { t } from 'src/translations/src'
import Image from 'src/shared/components/common/Image'
import {
  getAddressDisplay,
  toSavedAddress
} from 'src/public/components/online_ordering/addressUtils'
import { TailSpin } from 'react-loader-spinner'
import { UnifiedOrderStatus } from '../order_tracker/order_status_bar/statusUtil'
import { OrderHelpSection } from './OrderHelpSection'

export const BehaviorSection = ({
  deliveryDriver,
  unifiedOrderStatus
}: {
  deliveryDriver?: DeliveryDriver | null
  unifiedOrderStatus: UnifiedOrderStatus
}) => {
  const { ooRestaurant, selectedLocation } = useRestaurant()
  const { completedOrder } = useCompletedOrder()
  const tracker = useTracker()
  const { customer } = useCustomer()

  if (!completedOrder) return null

  const isDeliveryOrder =
    completedOrder?.diningOptionBehavior === DiningOptionBehavior.Delivery

  const rxAddress = formatAddress({
    ...completedOrder.restaurant.location,
    zipcode: completedOrder.restaurant.location.zip
  })

  const savedAddress = toSavedAddress(completedOrder?.deliveryInfo, customer)
  const { displayLabel, formattedAddress } = savedAddress
    ? getAddressDisplay(savedAddress)
    : { displayLabel: '', formattedAddress: '' }

  const destination = encodeURIComponent(
    [
      completedOrder.restaurant.location.address1,
      completedOrder.restaurant.location.address2 || '',
      completedOrder.restaurant.location.city,
      completedOrder.restaurant.location.state,
      completedOrder.restaurant.location.zip
    ].join(' ')
  )
  const directionsLink = `https://www.google.com/maps/dir/?api=1&destination=${destination}`

  const i18nCountryCode = (ooRestaurant?.i18n?.country || 'US') as CountryCode

  const parsedDriverPhoneNumber = deliveryDriver?.phoneNumber
    ? parsePhoneNumber(deliveryDriver.phoneNumber, i18nCountryCode)
    : null

  const instructions = isDeliveryOrder
    ? completedOrder.deliveryInfo?.notes
    : completedOrder.guestCommunication

  const isTDSOrder = isDeliveryOrder && !!completedOrder?.deliveryServiceInfo

  return (
    <div
      className='orderConfirmationSection mediumPadding leftAlign noXPadding'
      data-testid='behavior-section'
    >
      <div className='behaviorSectionContent'>
        <h2 className='behavior-section-title-checkout'>
          {isDeliveryOrder
            ? t('order-confirmation.behavior.delivery-details')
            : t('order-confirmation.behavior.pickup-details')}
        </h2>
        {isDeliveryOrder ? (
          <div className='behaviorSectionItem'>
            <LocationIcon accessibility='decorative' />
            <div className='textContainer'>
              <h3>{displayLabel}</h3>
              <p>{formattedAddress}</p>
            </div>
          </div>
        ) : (
          <Link
            href={directionsLink}
            data-testid='directions-button'
            className='behaviorSectionItem'
            aria-label={t('order-confirmation.behavior.get-directions')}
            onClick={() =>
              tracker.track(TrackingEvents.GOOGLE_MAPS_LINK_CLICKED, {
                context: 'order_confirmation_link',
                restaurant_guid: completedOrder?.restaurant?.guid ?? ''
              })
            }
          >
            <Image
              src='/icons/walking.svg'
              alt={t('order-confirmation.behavior.img-alt.pickup')}
              className='icon'
            />
            <div className='textContainer'>
              <h3>
                {getDisplayBusinessName(
                  completedOrder?.restaurant?.name,
                  selectedLocation?.name
                )}
              </h3>
              <p>{rxAddress}</p>
            </div>
            <SplitIcon accessibility='decorative' className='icon' />
          </Link>
        )}
        {isTDSOrder && unifiedOrderStatus !== UnifiedOrderStatus.SCHEDULED && (
          <>
            <div className='sectionSeparatorContainer'>
              <hr className='sectionSeparator' />
            </div>
            <div className='behaviorSectionItem'>
              <DeliveryIcon accessibility='decorative' />
              {deliveryDriver ? (
                <div className='textContainer'>
                  <div className='driverInfo'>
                    <h3>{deliveryDriver?.name}</h3>
                    {parsedDriverPhoneNumber?.isValid() && (
                      <Link
                        className='driverPhone'
                        href={`tel:${parsedDriverPhoneNumber.number}`}
                      >
                        {parsedDriverPhoneNumber.formatNational()}
                      </Link>
                    )}
                  </div>
                  <p>
                    {t('order-confirmation.behavior.driver-will-be-delivering')}
                  </p>
                </div>
              ) : (
                <>
                  <div className='textContainer'>
                    <h3>{t('order-confirmation.behavior.assigning-driver')}</h3>
                    <p>{t('order-confirmation.behavior.looking-for-driver')}</p>
                  </div>
                  <TailSpin
                    color={'#646464'}
                    width={'20px'}
                    height={'20px'}
                    strokeWidth={'2px'}
                  />
                </>
              )}
            </div>
          </>
        )}
        {instructions && (
          <>
            <div className='sectionSeparatorContainer'>
              <hr className='sectionSeparator' />
            </div>
            <div className='behaviorSectionItem'>
              <RestaurantMenuIcon accessibility='decorative' />
              <div className='textContainer'>
                <h3>
                  {isDeliveryOrder
                    ? t('order-confirmation.behavior.delivery-instructions')
                    : t('order-confirmation.behavior.pickup-instructions')}
                </h3>
                <p>{instructions}</p>
              </div>
            </div>
          </>
        )}
        {/* In-progress order: the phone card only. Recovery belongs to the completed-order
            mount in OrderConfirmation. No group wrapper here — the card is an item inside
            the behavior card, exactly where the inline block used to be. */}
        <OrderHelpSection allowRecoveryCard={false} />
      </div>
    </div>
  )
}
